import { Button, Modal } from '@morressier/alembic';
import { useToast } from '@morressier/alembic';
import { Flex, Text } from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClockFour } from '@fortawesome/pro-light-svg-icons';
import { useRequestDeadlineExtension } from 'src/api/commands';
import { useAssessmentData } from 'src/contexts/AssessmentDataProvider';
import { toUTCDateString } from '../../utils/date';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { getExtensionSchema } from '@publishing-engine/domain/validation';
import { FormFieldDate } from '../FormField';
import { getMaxUTCDateString } from '@publishing-engine/domain/utils';

interface RequestExtensionModalProps {
  isOpen: boolean;
  remainingDeadlineAutoExtension?: number;
  onClose: () => void;
}

export const RequestExtensionModal: React.FC<RequestExtensionModalProps> = ({
  isOpen,
  remainingDeadlineAutoExtension,
  onClose
}) => {
  const { earlyAssessment, organizationId } = useAssessmentData();
  const toast = useToast();

  const currentDeadline = toUTCDateString(
    earlyAssessment.workflow.currentDeadline ?? ''
  );

  const maxDate = getMaxUTCDateString(currentDeadline);

  const methods = useForm({
    defaultValues: {
      proposedDate: ''
    },
    resolver: zodResolver(getExtensionSchema(currentDeadline))
  });

  const requestDeadlineExtension = useRequestDeadlineExtension({
    submissionId: earlyAssessment?.caseData.submission.id ?? '',
    organizationId,
    reviewRequestId: earlyAssessment?.id ?? '',
    submissionContextId: earlyAssessment?.submissionContextId ?? ''
  });

  const onSubmit = async () => {
    const response = await requestDeadlineExtension.mutateAsync(
      methods.getValues().proposedDate
    );
    if (response.isSuccess) {
      let toastTitle = '';
      let toastDescription = '';
      if (remainingDeadlineAutoExtension) {
        toastTitle =
          remainingDeadlineAutoExtension !== 1
            ? 'Extension Granted'
            : 'Extension Requested';
        toastDescription =
          remainingDeadlineAutoExtension !== 1
            ? 'Your request for extension has been automatically granted.'
            : 'Your request was sent to Journal Editors.';
      } else {
        toastTitle = 'Extension Requested';
        toastDescription = 'Your request was sent to this Journal’s Editors.';
      }
      toast({
        variant: 'success',
        title: toastTitle,
        description: toastDescription,
        isClosable: true
      });
      onClose();
    } else {
      toast({
        variant: 'error',
        title: 'Error requesting extension',
        description: 'Please try again later.'
      });
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      width="28rem"
      closeButtonProps={{
        hidden: true
      }}
    >
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <Flex flexDirection="column" gap={8} padding={8}>
            <Flex>
              <FontAwesomeIcon fontSize={32} icon={faClockFour} />
            </Flex>
            <Flex flexDirection="column" gap={2}>
              <Text textStyle="headingGroup">Request Extension?</Text>
              <Text textStyle="textBody">
                This extension request will be reviewed by the editorial team,
                and you will be notified via email of their decision. If your
                extension request is approved, your deadline will be updated,
                and you will be notified of this change.
              </Text>
              <FormFieldDate
                config={{
                  name: 'proposedDate',
                  component: 'Custom:DateInput',
                  caseDataFields: {
                    main: 'proposedDate'
                  },
                  params: {
                    labels: {
                      main: 'Proposed Deadline Date',
                      subtitle: ''
                    },
                    validations: {
                      kind: 'date',
                      required: true,
                      min: currentDeadline,
                      max: maxDate
                    }
                  }
                }}
              />
            </Flex>
            <Flex justifyContent="flex-end" gap={2}>
              <Button variant="secondary" onClick={onClose}>
                Cancel
              </Button>
              <Button variant="primary" type="submit">
                Request Extension
              </Button>
            </Flex>
          </Flex>
        </form>
      </FormProvider>
    </Modal>
  );
};
